import React, { useState, useRef, useEffect } from 'react';
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';
import * as htmlToImage from 'html-to-image';
import { FiUpload, FiDownload, FiFileText, FiImage, FiPrinter, FiRefreshCw } from "react-icons/fi";
import { FaRegFileWord, FaPalette, FaFont, FaAlignLeft, FaAlignCenter, FaAlignRight } from "react-icons/fa";
import { useTheme } from '../../context/ThemeContext';
import HeaderSection from '../../components/HeaderSection/HeaderSection';
import BottomSection from '../../components/BottomSection/BottomSection';

// Font registration for PDF
const registerPDFFonts = async () => {
  try {
    const fonts = [
      { family: 'Caveat', src: '/fonts/Caveat-Regular.ttf' },
      { family: 'Dancing Script', src: '/fonts/DancingScript-Regular.ttf' },
      { family: 'Indie Flower', src: '/fonts/IndieFlower-Regular.ttf' },
      { family: 'Kalam', src: '/fonts/Kalam-Regular.ttf' },
      { family: 'Pacifico', src: '/fonts/Pacifico-Regular.ttf' },
      { family: 'Shadows Into Light', src: '/fonts/ShadowsIntoLight-Regular.ttf' },
      { family: 'Homemade Apple', src: '/fonts/HomemadeApple-Regular.ttf' },
      { family: 'Nothing You Could Do', src: '/fonts/NothingYouCouldDo-Regular.ttf' },
      { family: 'Nanum Pen Script', src: '/fonts/NanumPenScript-Regular.ttf' },
      { family: 'Reenie Beanie', src: '/fonts/ReenieBeanie-Regular.ttf' },
    ];

    for (const font of fonts) {
      if (!Font.getRegisteredFontFamilies().includes(font.family)) {
        await Font.register({
          family: font.family,
          src: font.src
        });
      }
    }
  } catch (error) {
    console.error('Error registering fonts for PDF:', error);
  }
};

// Load fonts for web preview
const loadWebFonts = async () => {
  try {
    const fontFaces = [
      { name: 'Caveat', url: '/fonts/Caveat-Regular.ttf' },
      { name: 'Dancing Script', url: '/fonts/DancingScript-Regular.ttf' },
      { name: 'Indie Flower', url: '/fonts/IndieFlower-Regular.ttf' },
      { name: 'Kalam', url: '/fonts/Kalam-Regular.ttf' },
      { name: 'Pacifico', url: '/fonts/Pacifico-Regular.ttf' },
      { name: 'Shadows Into Light', url: '/fonts/ShadowsIntoLight-Regular.ttf' },
      { name: 'Homemade Apple', url: '/fonts/HomemadeApple-Regular.ttf' },
      { name: 'Reenie Beanie', url: '/fonts/ReenieBeanie-Regular.ttf' },
      { name: 'Nothing You Could Do', url: '/fonts/NothingYouCouldDo-Regular.ttf' },
      { name: 'Nanum Pen Script', url: '/fonts/NanumPenScript-Regular.ttf' },
    ];

    for (const font of fontFaces) {
      const fontFace = new FontFace(font.name, `url(${font.url})`);
      try {
        const loadedFace = await fontFace.load();
        document.fonts.add(loadedFace);
      } catch (err) {
        console.error(`Failed to load font ${font.name}:`, err);
      }
    }
  } catch (error) {
    console.error('Error loading web fonts:', error);
  }
};

// PDF Document Component
const HandwritingDocument = ({ 
  text, 
  fontFamily, 
  inkColor, 
  paperType, 
  fontSize, 
  lineHeight,
  margin,
  textAlignment,
  textPadding
}) => {
  const words = text.split(' ');
  const pages = [];
  let currentPage = [];
  let wordCount = 0;

  words.forEach((word) => {
    currentPage.push(word);
    wordCount++;
    if (wordCount >= 250) {
      pages.push(currentPage.join(' '));
      currentPage = [];
      wordCount = 0;
    }
  });

  if (currentPage.length > 0) {
    pages.push(currentPage.join(' '));
  }

  const styles = StyleSheet.create({
    page: {
      padding: margin ? textPadding : 0,
      backgroundColor: paperType === 'yellow' ? '#ffffcc' : 
                      paperType === 'parchment' ? '#f5f5dc' :
                      paperType === 'newspaper' ? '#f8f8f8' :
                      '#ffffff',
    },
    plainPaper: {
      flex: 1,
    },
    linedPaper: {
      flex: 1,
      backgroundImage: 'linear-gradient(to bottom, transparent 95%, #e0e0e0 95%)',
      backgroundSize: `100% ${lineHeight * fontSize}px`,
    },
    graphPaper: {
      flex: 1,
      backgroundImage: 'linear-gradient(to right, #f0f0f0 1px, transparent 1px), linear-gradient(to bottom, #f0f0f0 1px, transparent 1px)',
      backgroundSize: '20px 20px',
    },
    yellowPaper: {
      flex: 1,
      backgroundImage: 'linear-gradient(to bottom, transparent 95%, #e0e0e0 95%)',
      backgroundSize: `100% ${lineHeight * fontSize}px`,
    },
    parchmentPaper: {
      flex: 1,
      backgroundImage: 'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiPgogIDxmaWx0ZXIgaWQ9Im5vaXNlIj4KICAgIDxmZVR1cmJ1bGVuY2UgdHlwZT0iZnJhY3RhbE5vaXNlIiBiYXNlRnJlcXVlbmN5PSIwLjA1IiBudW1PY3RhdmVzPSI1IiBzdGl0Y2hUaWxlcz0ic3RpdGNoIi8+CiAgICA8ZmVDb2xvck1hdHJpeCB0eXBlPSJzYXR1cmF0ZSIgdmFsdWVzPSIwIi8+CiAgPC9maWx0ZXI+CiAgPHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsdGVyPSJ1cmwoI25vaXNlKSIgb3BhY2l0eT0iMC4wNSIvPgo8L3N2Zz4=")',
    },
    newspaperPaper: {
      flex: 1,
      backgroundImage: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 1px, transparent 1px)',
      backgroundSize: `100% ${lineHeight * fontSize}px`,
    },
    text: {
      marginBottom: 20,
      textAlign: textAlignment,
    },
  });

  return (
    <Document>
      {pages.map((pageText, index) => (
        <Page key={index} size="A4" style={[styles.page, styles[`${paperType}Paper`]]}>
          <View>
            <Text
              style={{
                fontFamily: fontFamily,
                color: inkColor,
                fontSize: fontSize,
                lineHeight: lineHeight,
                textAlign: textAlignment,
              }}
            >
              {pageText}
            </Text>
          </View>
        </Page>
      ))}
    </Document>
  );
};

// Main App Component
const TextToHandwriting = () => {
  const { darkMode } = useTheme();
  const [text, setText] = useState('');
  const [fontFamily, setFontFamily] = useState('Caveat');
  const [inkColor, setInkColor] = useState('#000000');
  const [paperType, setPaperType] = useState('plain');
  const [fontSize, setFontSize] = useState(24);
  const [lineHeight, setLineHeight] = useState(1.5);
  const [margin, setMargin] = useState(true);
  const [textPadding, setTextPadding] = useState(40);
  const [textAlignment, setTextAlignment] = useState('left');
  const [showPreview, setShowPreview] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const previewRef = useRef(null);

  // Load fonts on component mount
  useEffect(() => {
    const loadFonts = async () => {
      await Promise.all([
        registerPDFFonts(),
        loadWebFonts()
      ]);
      setFontsLoaded(true);
    };
    
    loadFonts();
  }, []);

  const fontOptions = [
    { name: 'Caveat', value: 'Caveat', desc: 'Elegant cursive' },
    { name: 'Dancing Script', value: 'Dancing Script', desc: 'Neat handwriting' },
    { name: 'Indie Flower', value: 'Indie Flower', desc: 'Casual style' },
    { name: 'Kalam', value: 'Kalam', desc: 'Slanted script' },
    { name: 'Pacifico', value: 'Pacifico', desc: 'Stylish cursive' },
    { name: 'Shadows Into Light', value: 'Shadows Into Light', desc: 'Light and airy' },
    { name: 'Homemade Apple', value: 'Homemade Apple', desc: 'Clean print' },
    { name: 'Reenie Beanie', value: 'Reenie Beanie', desc: 'Child-like' },
    { name: 'Nothing You Could Do', value: 'Nothing You Could Do', desc: 'Casual script' },
    { name: 'Nanum Pen Script', value: 'Nanum Pen Script', desc: 'Korean-style' },
  ];

  const colorOptions = [
    { name: 'Black', value: '#000000' },
    { name: 'Blue', value: '#0000FF' },
    { name: 'Red', value: '#FF0000' },
    { name: 'Green', value: '#008000' },
    { name: 'Purple', value: '#800080' },
    { name: 'Brown', value: '#654321' },
  ];

  const paperOptions = [
    { name: 'Plain White', value: 'plain', desc: 'Standard paper' },
    { name: 'Lined', value: 'lined', desc: 'Notebook style' },
    { name: 'Graph', value: 'graph', desc: 'Grid paper' },
    { name: 'Yellow Pad', value: 'yellow', desc: 'Legal pad' },
    { name: 'Parchment', value: 'parchment', desc: 'Antique look' },
  ];

  const calculatePages = () => {
    if (!text) return 0;
    const words = text.split(' ');
    return Math.ceil(words.length / 250);
  };

  const handleTextChange = (e) => {
    setText(e.target.value);
  };

  const downloadAsImage = async (format = 'png') => {
    if (!previewRef.current || !fontsLoaded) return;
    
    try {
      // Ensure fonts are loaded before capturing
      await document.fonts.ready;
      
      let dataUrl;
      if (format === 'png') {
        dataUrl = await htmlToImage.toPng(previewRef.current);
      } else {
        dataUrl = await htmlToImage.toJpeg(previewRef.current);
      }
      
      const link = document.createElement('a');
      link.download = `handwriting.${format}`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Error generating image:', error);
      alert('Error generating image. Please try again.');
    }
  };

  const printPreview = () => {
    window.print();
  };

  // Inline styles for paper backgrounds
  const paperStyles = {
    plain: {
      backgroundColor: 'white',
    },
    lined: {
      backgroundColor: 'white',
      backgroundImage: `linear-gradient(to bottom, transparent 95%, #e0e0e0 95%)`,
      backgroundSize: `100% ${lineHeight * fontSize}px`,
    },
    graph: {
      backgroundColor: 'white',
      backgroundImage: `linear-gradient(#f0f0f0 1px, transparent 1px),
        linear-gradient(to right, #f0f0f0 1px, transparent 1px)`,
      backgroundSize: '20px 20px',
    },
    yellow: {
      backgroundColor: '#ffffcc',
      backgroundImage: `linear-gradient(to bottom, transparent 95%, #e0e0e0 95%)`,
      backgroundSize: `100% ${lineHeight * fontSize}px`,
    },
    parchment: {
      backgroundColor: '#f5f5dc',
      backgroundImage: `url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiPgogIDxmaWx0ZXIgaWQ9Im5vaXNlIj4KICAgIDxmZVR1cmJ1bGVuY2UgdHlwZT0iZnJhY3RhbE5vaXNlIiBiYXNlRnJlcXVlbmN5PSIwLjA1IiBudW1PY3RhdmVzPSI1IiBzdGl0Y2hUaWxlcz0ic3RpdGNoIi8+CiAgICA8ZmVDb2xvck1hdHJpeCB0eXBlPSJzYXR1cmF0ZSIgdmFsdWVzPSIwIi8+CiAgPC9maWx0ZXI+CiAgPHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsdGVyPSJ1cmwoI25vaXNlKSIgb3BhY2l0eT0iMC4wNSIvPgo8L3N2Zz4=")`,
    },
  };

  return (
    <>
      <HeaderSection toolId="textToHandwriting" />
      <div className="max-w-6xl mx-auto p-4 lg:max-w-full lg:px-4 dark:text-white">
        <div className={`bg-white dark:text-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden`}>
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
            <h1 className="text-2xl font-bold mb-2 flex items-center">
              <FaFont className="mr-3" />
              Text to Handwriting Converter
            </h1>
            <p className="opacity-90">
              Transform digital text into realistic handwritten notes
            </p>
          </div>

          {/* Main Content */}
          <div className="p-6">
            {/* Text Input Section at Top */}
            <div className="bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 p-4 rounded-lg mb-6">
              <h3 className="font-medium mb-3">Text Input</h3>
              <textarea
                value={text}
                onChange={handleTextChange}
                placeholder="Type or paste your text here..."
                className="w-full h-40 p-3 border border-gray-300 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              />
              <div className="flex justify-between mt-2 text-sm text-gray-600 dark:text-gray-300">
                <span>{text.length} characters</span>
                <span>{text.split(/\s+/).filter(Boolean).length} words</span>
                <span>{calculatePages()} {calculatePages() === 1 ? 'page' : 'pages'}</span>
              </div>
            </div>

            {/* Customization Options in Dropdown Sections */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Card 1: Handwriting Style */}
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold flex items-center mb-4">
                  <FaFont className="text-blue-500 mr-2" />
                  Handwriting Style
                </h2>

                {/* Font Selector */}
                <div className="space-y-2 mb-4">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Font Family</label>
                  <div className="relative">
                    <select
                      value={fontFamily}
                      onChange={(e) => setFontFamily(e.target.value)}
                      className="w-full p-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition appearance-none"
                      style={{ fontFamily }}
                    >
                      {fontOptions.map((font) => (
                        <option key={font.value} value={font.value}>
                          {font.name} – {font.desc}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute top-0 right-0 h-full flex items-center pr-4 text-gray-500">
                      <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Font Preview */}
                <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 text-center border border-gray-200 dark:border-gray-700">
                  <p
                    className="text-lg text-gray-700 dark:text-gray-200"
                    style={{ fontFamily }}
                  >
                    The quick brown fox jumps over the lazy dog.
                  </p>
                </div>
              </div>

              {/* Card 2: Ink & Paper */}
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold flex items-center mb-4">
                  <FaPalette className="text-pink-500 mr-2" />
                  Ink & Paper
                </h2>
                <div className="mb-5">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Ink Color</label>
                  <div className="flex flex-wrap gap-3">
                    {colorOptions.map((color) => (
                      <button
                        key={color.value}
                        onClick={() => setInkColor(color.value)}
                        className={`w-8 h-8 rounded-full border-2 transition-all duration-200 ${
                          inkColor === color.value
                            ? 'border-blue-500 scale-110'
                            : 'border-transparent hover:scale-105'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      />
                    ))}
                    <div className="relative">
                      <input
                        type="color"
                        value={inkColor}
                        onChange={(e) => setInkColor(e.target.value)}
                        className="absolute opacity-0 w-8 h-8 cursor-pointer"
                        id="customColor"
                      />
                      <label
                        htmlFor="customColor"
                        className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center cursor-pointer hover:border-blue-500 transition"
                        title="Choose custom color"
                      >
                        🎨
                      </label>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Paper Style</label>
                  <div className="relative">
                    <select
                      value={paperType}
                      onChange={(e) => setPaperType(e.target.value)}
                      className="w-full p-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition appearance-none"
                    >
                      {paperOptions.map((paper) => (
                        <option key={paper.value} value={paper.value}>
                          {paper.name} - {paper.desc}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute top-0 right-0 h-full flex items-center pr-4 text-gray-500">
                      <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 3: Text Formatting */}
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold flex items-center mb-4">
                  <FaAlignLeft className="text-green-500 mr-2" />
                  Text Formatting
                </h2>
                <div className="mb-5">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                    Font Size: <span className="font-bold">{fontSize}px</span>
                  </label>
                  <input
                    type="range"
                    min="12"
                    max="36"
                    value={fontSize}
                    onChange={(e) => setFontSize(parseInt(e.target.value))}
                    className="w-full accent-blue-500"
                  />
                </div>
                <div className="mb-5">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                    Line Spacing: <span className="font-bold">{lineHeight.toFixed(1)}</span>
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.1"
                    value={lineHeight}
                    onChange={(e) => setLineHeight(parseFloat(e.target.value))}
                    className="w-full accent-blue-500"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Text Alignment</label>
                  <div className="flex gap-3">
                    {[
                      { align: 'left', icon: <FaAlignLeft />, label: 'Left' },
                      { align: 'center', icon: <FaAlignCenter />, label: 'Center' },
                      { align: 'right', icon: <FaAlignRight />, label: 'Right' },
                    ].map(({ align, icon, label }) => (
                      <button
                        key={align}
                        onClick={() => setTextAlignment(align)}
                        className={`flex-1 p-2 rounded-lg border text-sm font-medium flex items-center justify-center transition ${
                          textAlignment === align
                            ? 'bg-blue-100 text-blue-700 border-blue-500 dark:bg-blue-900/30 dark:text-blue-300'
                            : 'bg-gray-100 dark:bg-gray-800 border-gray-300 hover:border-blue-300'
                        }`}
                      >
                        {icon}
                        <span className="ml-1">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Margins Toggle */}
            <div className="bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 p-4 rounded-lg mb-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={margin}
                    onChange={() => setMargin(!margin)}
                    className="mr-2 h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium">Enable Margins</span>
                </label>
                {margin && (
                  <div className="w-48">
                    <label className="block mb-1 text-sm font-medium">
                      Margin Size: {textPadding}px
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="100"
                      value={textPadding}
                      onChange={(e) => setTextPadding(parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Preview Section */}
            <div className="bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 p-4 rounded-lg mb-6 border border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-medium">Handwriting Preview</h3>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={showPreview}
                    onChange={() => setShowPreview(!showPreview)}
                    className="mr-2 h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium">Show Preview</span>
                </label>
              </div>
              
              {showPreview && (
                <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                  {text ? (
                    <div
                      ref={previewRef}
                      className="handwriting-preview mx-auto print:p-0 print:bg-white"
                      style={{
                        fontFamily: fontFamily,
                        color: inkColor,
                        fontSize: `${fontSize}px`,
                        lineHeight: lineHeight,
                        minHeight: '400px',
                        maxWidth: '800px',
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        textAlign: textAlignment,
                        padding: margin ? `${textPadding}px` : '0',
                        ...paperStyles[paperType]
                      }}
                    >
                      {text}
                    </div>
                  ) : (
                    <div className="text-center py-16 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg dark:border-gray-600">
                      <FiFileText className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-lg font-medium">No text entered</h3>
                      <p className="mt-1">Your handwritten preview will appear here</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Export Options */}
            <div className="bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h3 className="font-medium mb-4">Export Options</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  onClick={() => downloadAsImage('png')}
                  disabled={!text || !fontsLoaded}
                  className={`bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition flex items-center justify-center ${!text || !fontsLoaded ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <FiImage className="mr-2" /> PNG Image
                </button>
                <button
                  onClick={() => downloadAsImage('jpeg')}
                  disabled={!text || !fontsLoaded}
                  className={`bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-lg font-medium transition flex items-center justify-center ${!text || !fontsLoaded ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <FiImage className="mr-2" /> JPG Image
                </button>
                <button
                  onClick={printPreview}
                  disabled={!text}
                  className={`bg-gray-600 hover:bg-gray-700 text-white py-3 px-4 rounded-lg font-medium transition flex items-center justify-center ${!text ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <FiPrinter className="mr-2" /> Print
                </button>
                {text ? (
                  <button
                    onClick={() => setIsGeneratingPDF(true)}
                    disabled={isGeneratingPDF || !fontsLoaded}
                    className={`bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg font-medium transition flex items-center justify-center ${isGeneratingPDF || !fontsLoaded ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isGeneratingPDF ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Preparing PDF...
                      </>
                    ) : (
                      <>
                        <FaRegFileWord className="mr-2" /> Download PDF
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    disabled
                    className="bg-gray-400 text-white py-3 px-4 rounded-lg font-medium cursor-not-allowed"
                  >
                    <FaRegFileWord className="mr-2" /> Download PDF
                  </button>
                )}
              </div>

              {isGeneratingPDF && text && (
                <PDFDownloadLink
                  document={
                    <HandwritingDocument
                      text={text}
                      fontFamily={fontFamily}
                      inkColor={inkColor}
                      paperType={paperType}
                      fontSize={fontSize}
                      lineHeight={lineHeight}
                      margin={margin}
                      textPadding={textPadding}
                      textAlignment={textAlignment}
                    />
                  }
                  fileName="handwritten-note.pdf"
                  className="hidden"
                  onRender={() => setIsGeneratingPDF(false)}
                >
                  {({ loading }) => null}
                </PDFDownloadLink>
              )}

              <div className="mt-4">
                <button
                  onClick={() => {
                    setText('');
                    setFontFamily('Caveat');
                    setInkColor('#000000');
                    setPaperType('plain');
                    setFontSize(24);
                    setLineHeight(1.5);
                    setTextAlignment('left');
                    setMargin(true);
                    setTextPadding(40);
                  }}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-lg font-medium transition flex items-center justify-center"
                >
                  <FiRefreshCw className="mr-2" /> Reset All
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-100 dark:text-white dark:bg-gray-800 p-4 text-center text-sm text-gray-600">
            <p>Convert digital text to realistic handwritten notes with customizable styles</p>
          </div>
        </div>
      </div>
      <BottomSection toolId="textToHandwriting" />
    </>
  );
};

export default TextToHandwriting;